import axios, { AxiosRequestConfig, Method } from "axios";
import qs from "qs";

import { createResponseErrorInterceptor, createResponseInterceptor, getApiHeader, isFormData } from "./utils";
const BASE_URL = process.env.BIG_FLIP_BASE_API_URL;

type CallObjArgs = {
  method: Method;
  endpoint: string;
  customHeaders: AxiosRequestConfig["headers"];
  params?: any;
  data?: any;
  additionalConfig?: AxiosRequestConfig;
};

export class RequestManager {
  baseUrl: string;
  seekCredential: () => Promise<void>;
  interceptorRequest: (config: any) => any;
  interceptorRequestError: (err: any) => Promise<never>;
  interceptorResponse: (response: any) => any;
  interceptorResponseError: (error: any) => Promise<never>;

  constructor(
    url = BASE_URL,
    interceptorResponse = createResponseInterceptor,
    interceptorResponseError = createResponseErrorInterceptor
  ) {
    this.baseUrl = url;
    this.seekCredential = async () => {};
    this.interceptorRequest = (config) => config;
    this.interceptorRequestError = (err) => Promise.reject(err);
    this.interceptorResponse = interceptorResponse;
    this.interceptorResponseError = interceptorResponseError;
  }

  setInterceptorRequest(fn: () => void, fnE: () => Promise<never>) {
    if (fn !== undefined) this.interceptorRequest = fn;
    if (fnE !== undefined) this.interceptorRequestError = fnE;
  }

  setInterceptorResponse(fn: (response: any) => any, fnE: (error: any) => Promise<never>) {
    if (fn) this.interceptorResponse = fn;
    if (fnE) this.interceptorResponseError = fnE;
  }

  async call<Response>(obj: CallObjArgs) {
    const headers = { ...getApiHeader(), ...obj.customHeaders };
    const url = this.baseUrl + obj.endpoint;
    const method = obj.method || "get";
    // FormData passes through untouched; JSON bodies pass through as objects so axios serializes
    // them to JSON; everything else stays form-urlencoded via qs.stringify (default behaviour).
    const isJsonBody = `${headers["content-type"] || ""}`.includes("application/json");
    const data = isFormData(headers) || isJsonBody ? obj.data : qs.stringify(obj.data);
    const params = obj.params;

    const config: AxiosRequestConfig = {
      url,
      method,
      data,
      params,
      headers,
      timeout: 0,
      responseType: "json",
      ...obj.additionalConfig
    };

    const instance = axios.create();
    instance.interceptors.request.use(this.interceptorRequest, this.interceptorRequestError);
    instance.interceptors.response.use(this.interceptorResponse, this.interceptorResponseError);

    return instance.request<unknown, Response>(config);
  }

  // generic "Response" for type of expected response data object
  async get<Response = any>(
    endpoint: string,
    params = {},
    customHeaders = {},
    additionalConfig: AxiosRequestConfig = {}
  ) {
    return await this.call<Response>({ method: "GET", endpoint, params, customHeaders, additionalConfig });
  }

  async post<Response = any>(
    endpoint: string,
    data = {},
    customHeaders = {},
    additionalConfig: AxiosRequestConfig = {}
  ) {
    return await this.call<Response>({ method: "POST", endpoint, data, customHeaders, additionalConfig });
  }

  async put<Response = any>(
    endpoint: string,
    data = {},
    customHeaders = {},
    additionalConfig: AxiosRequestConfig = {}
  ) {
    return await this.call<Response>({ method: "PUT", endpoint, data, customHeaders, additionalConfig });
  }

  async delete<Response = any>(
    endpoint: string,
    params = {},
    customHeaders = {},
    additionalConfig: AxiosRequestConfig = {}
  ) {
    return await this.call<Response>({ method: "DELETE", endpoint, params, customHeaders, additionalConfig });
  }
}

const request = new RequestManager();
export default request;
